import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useNexOs } from "@/lib/nexOs";
import { useOps } from "@/lib/opsCards";
import { SYSTEM_CONSOLE_ID, moveItem } from "@/lib/placement";

// SA picker: move a card to another engine tab or the System Console.
export default function MoveToDialog({ item, onClose }) {
  const { schema } = useNexOs();
  const ops = useOps();
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const options = [
    ...(schema?.verticals || []).map((v) => ({ id: v.id, label: v.label })),
    { id: SYSTEM_CONSOLE_ID, label: "SYSTEM CONSOLE" },
  ].filter((o) => o.id !== item.vertical_id);

  const confirm = async () => {
    setBusy(true);
    try {
      await moveItem(item.id, target);
      await ops.reload();
      toast.success(`${item.label} moved to ${options.find((o) => o.id === target)?.label}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move card");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="move-to-dialog" className="bg-navy-900 border-slate-700 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif">Move “{item.label}”</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Only its placement changes. Data, window type and settings stay the same. It goes to the bottom of the destination.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5" data-testid="move-to-options">
          {options.map((o) => (
            <button key={o.id} type="button" onClick={() => setTarget(o.id)} data-testid={`move-to-option-${o.id}`}
              className={`text-left px-3 py-2 rounded-lg border font-mono text-xs tracking-[0.12em] uppercase transition-colors ${
                target === o.id ? "border-brand-cyan bg-brand-cyan/10 text-brand-cyan" : "border-slate-800 bg-navy-950 text-slate-300 hover:border-slate-600"}`}>
              {o.label}
            </button>
          ))}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} data-testid="move-to-cancel"
            className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 text-xs">Cancel</button>
          <button type="button" disabled={!target || busy} onClick={confirm} data-testid="move-to-confirm"
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 text-xs font-semibold disabled:opacity-40">
            {busy ? "Moving…" : "Move card"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
